import { useEffect, useRef, useState } from 'react';

/** Where the per-station Local Hardware Bridge listens. Overridable per machine via
 * localStorage('ims.hardwareBridgeUrl') for a station that had to move off the default port. */
export const HARDWARE_BRIDGE_URL =
  (typeof localStorage !== 'undefined' && localStorage.getItem('ims.hardwareBridgeUrl')) || 'http://127.0.0.1:9110';

export interface LiveWeightReading {
  raw: string;
  weight: number | null;
  unit: string | null;
  /** Null only when the scale reports no stability token AND the agent hasn't yet seen enough
   * consecutive identical readings to decide — treat null as "not stable" for capture purposes. */
  stable: boolean | null;
  timestamp: string;
}

/** Sends printer-language commands to the station's printer through the agent — no print dialog,
 * no driver rasterisation. Throws with the agent's own message so the caller can surface exactly
 * why a label didn't come out (wrong printer name, spooler error, agent down). */
export async function printRawViaBridge(commands: string, printerName?: string, documentName?: string): Promise<void> {
  const response = await fetch(`${HARDWARE_BRIDGE_URL}/print/raw`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ commands, printerName, documentName }),
    signal: AbortSignal.timeout(10_000),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.error ?? `Hardware Bridge returned ${response.status}`);
  }
}

/** Drops and reopens the agent's handle on the COM port. Fixes a wedged/replugged adapter; it cannot
 * free a port that a DIFFERENT process (a terminal, a vendor utility) is holding — only that process
 * can release its own handle. */
export async function reconnectBridgeSerial(): Promise<void> {
  const response = await fetch(`${HARDWARE_BRIDGE_URL}/serial/reconnect`, { method: 'POST', signal: AbortSignal.timeout(5000) });
  if (!response.ok) throw new Error(`Hardware Bridge returned ${response.status}`);
}

/** Releases the port and keeps it released, so another tool can use it without stopping the agent. */
export async function releaseBridgeSerial(): Promise<void> {
  const response = await fetch(`${HARDWARE_BRIDGE_URL}/serial/disconnect`, { method: 'POST', signal: AbortSignal.timeout(5000) });
  if (!response.ok) throw new Error(`Hardware Bridge returned ${response.status}`);
}

export interface HardwareBridgeStatus {
  agent: string;
  version: string;
  station: { code: string; name: string };
  serial: {
    port: string; baudRate: number; connected: boolean;
    /** True when an operator deliberately released the port, as opposed to a connection fault. */
    released: boolean;
    lastError: string | null; lastReadingAt: string | null;
    availablePorts: string[];
  };
  /** Real Windows spooler state, not an echo of the agent's config. `online` means Windows sees no
   * problem — for a USB label printer that is not the same as "a label will definitely come out",
   * since a powered-off unit often still reports Ready. `queuedJobs` growing is the better tell. */
  printer: { name: string; exists: boolean; online: boolean; status: string; problem: string | null; queuedJobs: number };
}

/**
 * Subscribes to the local Hardware Bridge's SSE weight feed, falling back cleanly when no agent is
 * running (the kiosk then stays in manual-entry mode rather than blocking production).
 *
 * EventSource is used rather than a poll because the scale streams continuously and reconnects are
 * handled by the browser itself — an agent restart mid-shift recovers without touching the page.
 */
export function useLiveWeight() {
  const [status, setStatus] = useState<HardwareBridgeStatus | null>(null);
  const [reading, setReading] = useState<LiveWeightReading | null>(null);
  const [streamConnected, setStreamConnected] = useState(false);
  const sourceRef = useRef<EventSource | null>(null);

  // Status poll doubles as the agent-presence check — it's what decides live vs manual mode, and
  // keeps reporting a scale that's unplugged while the agent itself is still up.
  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const response = await fetch(`${HARDWARE_BRIDGE_URL}/status`, { signal: AbortSignal.timeout(2500) });
        if (!response.ok) throw new Error(String(response.status));
        const data = (await response.json()) as HardwareBridgeStatus;
        if (!cancelled) setStatus(data);
      } catch {
        if (!cancelled) setStatus(null);
      }
    };

    void poll();
    const timer = window.setInterval(poll, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    // Only hold a stream open while the agent is actually answering — otherwise EventSource
    // retries a dead endpoint forever and fills the console with failures.
    if (!status) {
      sourceRef.current?.close();
      sourceRef.current = null;
      setStreamConnected(false);
      setReading(null);
      return;
    }
    if (sourceRef.current) return;

    const source = new EventSource(`${HARDWARE_BRIDGE_URL}/weight/stream`);
    sourceRef.current = source;
    source.onopen = () => setStreamConnected(true);
    source.onmessage = (event) => {
      try {
        setReading(JSON.parse(event.data) as LiveWeightReading);
      } catch {
        // A malformed frame shouldn't tear the stream down; the next one will be fine.
      }
    };
    source.onerror = () => setStreamConnected(false);

    return () => {
      source.close();
      sourceRef.current = null;
      setStreamConnected(false);
    };
  }, [status]);

  return {
    /** True when a Hardware Bridge is reachable — the kiosk switches to live capture on this. */
    agentAvailable: status !== null,
    /** True when the agent has the COM port open (agent can be up while the scale is unplugged). */
    scaleConnected: status?.serial.connected ?? false,
    scaleError: status?.serial.lastError ?? null,
    status,
    reading,
    streamConnected,
  };
}
